/**
 * Faz 4 İP3 (DEV-3217) — sorgu cevabının KAYNAĞINI okuyan saf yardımcılar.
 *
 * `UyapRequest`, cevabı sunucu snapshot'ından kurduğunda gövdeye iki alan
 * koyuyor: `snapshotKaynagi` (true) ve `snapshotDamgasi` (ISO). CANLI cevapta bu
 * alanlar yoktur — bugünkü işaretsiz davranış korunur.
 *
 * IndexedDB'den gelen cevapta ise DURUMA BAĞLI: snapshot yolundan dönen gövde
 * aynen cache'e de yazıldığı için, o kayıt okunduğunda rozet görünmeye devam
 * eder. Bu BİLİNÇLİ ve doğrudur — veri gerçekten o damgadaki koşumdan geliyor
 * ve o zamandan beri UYAP'tan yeniden çekilmedi; kullanıcı neye baktığını
 * bilmeye devam etsin. Canlı sorgu cevabı cache'i üzerine yazdığında işaret
 * kendiliğinden kalkar.
 *
 * Mantık bileşenden ayrı tutuldu: suite'te mount kullanılmıyor (saf modül
 * deseni), rozetin karar mantığı da birim testiyle korunabilsin diye.
 *
 * DEV-3346-B24: rozet her yaşta aynı nötr renkte kalıyor ve tooltip'i sabitti
 * — dünkü veri ile günler önceki veri görsel olarak ayırt edilemiyordu.
 * Aşağıdaki üç yardımcı (`kaynakYasiMetni`, `kaynakBayatMi`,
 * `kaynakTazeKadarMetni`) bu boşluğu dolduruyor; `tazeMi()` (tazelik.ts) ile
 * AYNI kaynağı (`snapshotDamgasi`/`snapshotTazeKadar`) okur, tazelik KURALINI
 * ise tekrarlamak yerine oradan İTHAL EDER — tek doğru kaynak `tazelik.ts`
 * kalsın diye (bkz. dosya başı v1/v2 notu, 9 Eyl 2026 kullanıcı kararı).
 */

import {istanbulGunu, tazeMi} from "@/plugins/uyap-plugin/snapshot/tazelik";

/** Cevap sunucu snapshot'ından mı geldi? */
export function snapshottanMi(cevap: any): boolean {
    return !!(cevap && cevap.snapshotKaynagi === true);
}

/**
 * Rozette gösterilecek tarih metni ("28.08.2026").
 *
 * Damga yok/bozuksa "kayıtlı" döner: rozet yine görünür (veri gerçekten
 * snapshot'tan geliyor) ama uydurma bir tarih yazılmaz.
 */
export function kaynakTarihiMetni(cevap: any): string {
    const damga = cevap?.snapshotDamgasi;
    if (!damga) {
        return "kayıtlı";
    }

    const tarih = new Date(damga);
    if (isNaN(tarih.getTime())) {
        return "kayıtlı";
    }

    // Gün sınırı İstanbul'a göre değerlendiriliyor (tazelik.ts); gösterim de öyle.
    return tarih.toLocaleDateString("tr-TR", {timeZone: "Europe/Istanbul"});
}

/**
 * İki damga arasındaki İstanbul TAKVİM GÜNÜ farkı (23:30 → ertesi gün 00:30 = 1).
 *
 * `istanbulGunu()`'nün "YYYY-MM-DD" çıktısı her zaman UTC gece yarısına
 * indirgenerek karşılaştırılıyor: bu biçimde `Date` ayrıştırması ECMAScript
 * spesifikasyonu gereği HER ZAMAN UTC kabul edilir, dolayısıyla iki günün
 * farkı yerel saat diliminden bağımsız, tam gün sayısı olarak çıkar.
 */
function istanbulGunFarki(damgaGunu: string, simdiGunu: string): number {
    const GUN_MS = 24 * 60 * 60 * 1000;
    const damgaUtc = new Date(`${damgaGunu}T00:00:00Z`).getTime();
    const simdiUtc = new Date(`${simdiGunu}T00:00:00Z`).getTime();

    return Math.round((simdiUtc - damgaUtc) / GUN_MS);
}

/**
 * Rozette gösterilecek yaş metni ("bugün" / "dün" / "N gün önce").
 *
 * Kural takvim günü FARKINA dayanır (tazelik kararına DEĞİL — o `tazeMi()`nin
 * kayan saat penceresi, bu ise kullanıcıya "hangi gün" izlenimi vermek için
 * ayrı bir gösterim kararı): 23:30 İstanbul'da alınan bir kayıt ertesi gün
 * 00:30'da görüntülense bile aradan yalnız 1 saat geçmiştir ama takvimde
 * "dün" denir — kullanıcı buna alışkın (dosya başı tazelik.ts v1/v2 notu).
 *
 * Damga yok/bozuksa "kayıtlı" döner: uydurma bir tarih/yaş yazılmaz. `simdi`
 * damgadan ÖNCEYSE (saat kayması) ise negatif yaş yerine "bugün" döner —
 * "kayıtlı" DEĞİL (bkz. altta `fark <= 0` dalı ve aynı senaryoyu adlandıran
 * test: "saat kayması (simdi damgadan önce) negatif yaş yerine 'bugün' döner").
 */
export function kaynakYasiMetni(cevap: any, simdi: Date = new Date()): string {
    const damgaGunu = istanbulGunu(cevap?.snapshotDamgasi);
    if (damgaGunu === null) {
        return "kayıtlı";
    }

    const simdiGunu = istanbulGunu(simdi);
    if (simdiGunu === null) {
        return "kayıtlı";
    }

    const fark = istanbulGunFarki(damgaGunu, simdiGunu);
    if (fark <= 0) {
        return "bugün";
    }
    if (fark === 1) {
        return "dün";
    }

    return `${fark} gün önce`;
}

/**
 * Rozet BAYAT mı (tazelik penceresi doldu mu)?
 *
 * Kural `tazeMi()` (tazelik.ts) ile BİREBİR AYNI — burada tekrarlanmıyor,
 * çağrılıyor: `snapshotTazeKadar` çözülüyorsa TEK ölçüt odur (şimdi ≥
 * taze_kadar → bayat); çözülmüyorsa (alan yok/bozuk) 24 saatlik sabit
 * pencereye (`TAZELIK_PENCERESI_MS`) düşülür. Okuma zinciri zaten bayat
 * snapshot'ı servis etmiyor (`UyapRequest.snapshotCevabiKur` → `tazeMi()`
 * kontrolü) — bu yüzden true'ya burada nadiren düşülür (ör. IndexedDB'den
 * okunan eski gövde gibi sınır durumlar); yine de kural doğru olmalı.
 */
export function kaynakBayatMi(cevap: any, simdi: Date = new Date()): boolean {
    const kayitGibi = {
        fetched_at: cevap?.snapshotDamgasi ?? null,
        taze_kadar: cevap?.snapshotTazeKadar ?? null,
    };

    return !tazeMi(kayitGibi, simdi);
}

/**
 * Tooltip için "taze kalma" bilgisi: "DD.MM.YYYY HH:MM'e kadar taze".
 *
 * `snapshotTazeKadar` çözülmüyorsa (alan yok/bozuk — sabit 24 saatlik
 * pencereye düşülen durum) boş dize döner: sunucunun hesaplamadığı bir tarihi
 * uydurmayız, tooltip o cümleyi atlar.
 */
export function kaynakTazeKadarMetni(cevap: any): string {
    const tazeKadar = cevap?.snapshotTazeKadar;
    if (!tazeKadar) {
        return "";
    }

    const tarih = new Date(tazeKadar);
    if (isNaN(tarih.getTime())) {
        return "";
    }

    const tarihMetni = tarih.toLocaleDateString("tr-TR", {timeZone: "Europe/Istanbul"});
    const saatMetni = tarih.toLocaleTimeString("tr-TR", {
        timeZone: "Europe/Istanbul",
        hour: "2-digit",
        minute: "2-digit",
    });

    return `${tarihMetni} ${saatMetni}'e kadar taze`;
}
